import { z } from 'zod'

export const JobTypeSchema = z.enum([
  'chat', 'research', 'summarize', 'review_pr', 'implement_issue',
  'fix_bug', 'write_doc', 'run_test', 'analyze_logs',
  'update_skill', 'update_memory',
])
export type JobType = z.infer<typeof JobTypeSchema>

export const JobStatusSchema = z.enum([
  'created', 'planned', 'awaiting_approval', 'queued', 'claimed',
  'running', 'completed', 'failed', 'cancelled', 'evaluated', 'learned',
])
export type JobStatus = z.infer<typeof JobStatusSchema>

export const RiskLevelSchema = z.enum(['low', 'medium', 'high'])
export type RiskLevel = z.infer<typeof RiskLevelSchema>

export const CreateJobRequestSchema = z.object({
  type: JobTypeSchema,
  project: z.string().min(1),
  input: z.record(z.unknown()),
  risk_level: RiskLevelSchema.default('low'),
})
export type CreateJobRequest = z.infer<typeof CreateJobRequestSchema>

export const JobSchema = z.object({
  id: z.string().uuid(),
  type: JobTypeSchema,
  status: JobStatusSchema,
  risk_level: RiskLevelSchema,
  project: z.string().nullable(),
  input: z.record(z.unknown()),
  result: z.record(z.unknown()).nullable(),
  assigned_executor: z.string().nullable(),
  artifact_r2_prefix: z.string().nullable(),
  // ISSUE #21 (Worker 側で組み立て) / #22 (Executor 側で消費)。
  // jobs テーブルの `selected_memories_json` 列をそのまま中継する文字列。
  // Worker は POST /api/jobs 時に `JSON.stringify(ContextPacket)` を保存し、
  // Executor は `GET /api/jobs/next` / `POST /api/jobs/:id/claim` の response から
  // 取り出して `--append-system-prompt` で Claude Code に注入する。
  // 古い row や AI Search 未設定環境では null になり得るので nullable + optional。
  selected_memories_json: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type Job = z.infer<typeof JobSchema>

// ISSUE #7: Executor が job を claim するときの body schema。
// Phase 1 では body の `executor_id` をそのまま `assigned_executor` に保存するが、
// ISSUE #9 (Access middleware) で Cloudflare Access Service Token JWT の
// `common_name` を真の identity として使うよう切り替える。
export const ClaimJobRequestSchema = z.object({
  executor_id: z.string().min(1),
})
export type ClaimJobRequest = z.infer<typeof ClaimJobRequestSchema>

// ISSUE #7: Executor が job を completed/failed に遷移させるときの body schema。
// Codex feedback (Fix 1): Phase 1 では JWT 由来の identity が無いため、body の
// `executor_id` を `assigned_executor` と照合することで「他人の job を complete」
// を防ぐ。ISSUE #9 で JWT 化したあとは、`executor_id` は照合用 (不一致なら 400) に
// 切り替わる。
export const CompleteJobRequestSchema = z.object({
  executor_id: z.string().min(1),
  status: z.enum(['completed', 'failed']),
  result: z.record(z.unknown()).optional(),
  artifact_r2_prefix: z.string().optional(),
})
export type CompleteJobRequest = z.infer<typeof CompleteJobRequestSchema>
